

import React, { useState, useEffect } from "react";
import "./Login.css";
import { 
  TextField, 
  Button, 
  InputAdornment, 
  IconButton,
  Alert,
  CircularProgress 
} from "@mui/material";
import { 
  Visibility, 
  VisibilityOff, 
  Email, 
  Lock, 
  WavingHand 
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    emailOrPhone: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    emailOrPhone: "",
    password: "",
  });
  const [apiError, setApiError] = useState("");

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const togglePassword = () => setShowPassword(!showPassword);

  const handleSignUpClick = () => {
    navigate("/signup");
  };

  const handleForgotPasswordClick = () => {
    navigate("/forgot");
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
    setApiError("");
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Email or Phone validation
    if (!formData.emailOrPhone.trim()) {
      newErrors.emailOrPhone = "Email or phone number is required";
    } else {
      const isEmail = formData.emailOrPhone.includes("@");
      const isPhone = /^[6-9]\d{9}$/.test(formData.emailOrPhone);
      
      if (!isEmail && !isPhone) {
        newErrors.emailOrPhone = "Please enter a valid email or 10-digit phone number";
      }
    }

    // Password validation
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const isEmail = formData.emailOrPhone.includes("@");
      
      const response = await axios.post(
        "https://api.rismicmart.com/api/delivery-person/login",
        {
          email: isEmail ? formData.emailOrPhone : undefined,
          phone: !isEmail ? formData.emailOrPhone : undefined,
          password: formData.password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        const { token, data } = response.data;
        
        // Save user data and token using auth context
        login(data, token);
        
        // Redirect to the page they tried to visit or dashboard
        const from = location.state?.from?.pathname || "/dashboard";
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error("Login error:", error);

      if (error.response?.data) {
        // Handle specific error messages from backend
        if (error.response.data.errors) {
          const backendErrors = {};
          error.response.data.errors.forEach((err) => {
            backendErrors[err.param || err.path] = err.msg || err.message;
          });
          setErrors((prev) => ({ ...prev, ...backendErrors }));
        }
        setApiError(
          error.response.data.message || "Login failed. Please try again."
        );
      } else if (error.request) {
        // Request made but no response received
        setApiError(
          "Cannot connect to server. Please ensure the backend is running on https://api.rismicmart.com"
        );
      } else {
        // Error in request setup
        setApiError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h2 className="login-title animated-text">
          Welcome Back <WavingHand className="wave-icon" />
        </h2>
        <p className="login-subtitle">Log in to continue your journey</p>

        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiError}
          </Alert>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          <TextField
            label="Email or Mobile Number"
            name="emailOrPhone"
            variant="outlined"
            fullWidth
            value={formData.emailOrPhone}
            onChange={handleChange}
            error={!!errors.emailOrPhone}
            helperText={errors.emailOrPhone}
            autoComplete="username"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email style={{ color: "#007b68" }} />
                </InputAdornment>
              ),
            }}
            className="login-input"
          />

          <TextField
            label="Password"
            name="password"
            variant="outlined"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            autoComplete="current-password"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock style={{ color: "#007b68" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePassword} edge="end" disabled={loading}>
                    {showPassword ? (
                      <Visibility style={{ color: "#007b68" }} />
                    ) : (
                      <VisibilityOff style={{ color: "#007b68" }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            className="login-input"
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            className="login-btn"
            disabled={loading}
            sx={{ mt: 3, position: "relative" }}
          >
            {loading ? (
              <>
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    left: "50%",
                    marginLeft: "-12px",
                    color: "#fff",
                  }}
                />
                LOGGING IN...
              </>
            ) : (
              "LOGIN"
            )}
          </Button>

          <div className="login-links">
            <p>
              Don't have an account?{" "}
              <span className="signup-link" onClick={handleSignUpClick}>
                Sign Up
              </span>
            </p>
            <p className="forgot" onClick={handleForgotPasswordClick}>
              Forgot password?
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;

